import React from 'react'
import { Stack } from 'expo-router'
import { ScrollView, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native'
import { PlayerDotsStrip } from '../../../components/game/PlayerDotsStrip'
import { EventLogRow } from '../../../components/game/EventLogRow'
import { FormattedLogText } from '../../../components/game/FormattedLogText'
import { NotificationBannerContent } from '../../../components/ui/NotificationBanner'
import { notificationTopBarPlacement } from '../../../lib/notificationBanner'
import { GameBanner } from '../../../components/game/GameBanner'
import { useTheme } from '../../../hooks/useTheme'
import { game } from '../../../preview/fixtures/cardBonusOnly'
import { buildActiveGameViewProps } from '../../../preview/buildActiveGameViewProps'
import type { GameState } from '../../../lib/gameState'
import type { LogRow } from '../../../lib/logFeed'

const players = ['E2E Tester', 'Tamerlane', 'Genghis', 'Boudica', 'Zanzibar', 'Hannibal'].map((displayName, index) => ({
  ...game.players[0]!, id: `visual-${index}`, userId: `visual-user-${index}`,
  displayName, slot: index + 1, playerIndex: index, colorSlot: index + 1,
  // No presence animation: only static pixels belong in a baseline.
  isBot: false,
}))
const previewState = buildActiveGameViewProps(game).gameState
const state: GameState = {
  ...previewState, players: players.map(player => String(player.slot)), turnIndex: 0,
  map: { ...previewState.map, territories: previewState.map.territories.map((territory, index) => ({ ...territory, ownerId: String(index % 6 + 1) })) },
}
const playerBySlot = new Map(players.map(player => [String(player.slot), player]))
const rows: LogRow[] = [
  'Genghis attacks neutral (Musket → Mortar)\n[6][4][2] vs [5][1] (1 lost, 1 lost)',
  'Tamerlane attacks Boudica (Saint Louis → Northern Highlands)\n[12][10] vs [9][8] (0 lost, 2 lost)',
].map((primary, index) => ({
  key: `visual-row-${index}`, primary, sub: index === 0 ? ['Genghis conquers Mortar (moved 2 armies)'] : [],
  kinds: ['attack'], turn: 1, ts: Date.UTC(2099, 0, 1), isTurnStart: false, isGameEnd: false,
  playerId: index === 0 ? '3' : '2', defenderId: index === 0 ? '' : '4', eventIndex: index,
  relatedPlayerIds: index === 0 ? ['3'] : ['2', '4'],
}))

/** Static fixtures use the actual production components, without API/bot clocks. */
export default function NativeRegressionsPreview() {
  const { theme, setTheme } = useTheme()
  const { fontScale } = useWindowDimensions()
  const notificationHeight = notificationTopBarPlacement({ pathname: '/games/fixture', insetTop: 0, fontScale }).height
  const ink = theme === 'dark' ? '#e6e1c4' : '#18170f'
  return (
    <ScrollView className="flex-1 bg-paper">
      <Stack.Screen options={{ title: 'Native visual checkpoints' }} />
      <View className="flex-row gap-4 p-3">
        {(['light', 'dark'] as const).map(mode => (
          <TouchableOpacity key={mode} testID={`visual-theme-${mode}`} accessibilityRole="button" onPress={() => setTheme(mode)}>
            <Text className="font-typed text-ink">{mode}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <View testID="native-visual-fixture" collapsable={false} className="bg-paper px-3 py-2 gap-2">
        {/* Keep the strip's overflowing unread badge inside the screenshot crop. */}
        <View testID="visual-player-bar" collapsable={false} style={{ height: 56, paddingRight: 8, flexDirection: 'row', alignItems: 'center' }}>
          <Text style={{ width: 132 }} className="font-stencil text-xs text-ink">6 players</Text>
          <PlayerDotsStrip state={state} players={players} onOpen={() => {}} unreadTotal={12} />
        </View>
        <Text className="font-stencil text-xs text-ink">Full log</Text>
        <View testID="visual-full-log" collapsable={false}>
          {rows.map(row => <EventLogRow key={`full-${row.key}`} row={row} playerBySlot={playerBySlot} />)}
        </View>
        <Text className="font-stencil text-xs text-ink">Compact log</Text>
        <View testID="visual-compact-log" collapsable={false}>
          {rows.map(row => <EventLogRow key={`compact-${row.key}`} row={row} playerBySlot={playerBySlot} variant="compact" />)}
        </View>
        <Text className="font-stencil text-xs text-ink">Attack roll</Text>
        <View testID="visual-attack-roll" collapsable={false}>
          <FormattedLogText className="font-typed text-ink" text="1. [6][4][2] vs [5][1] (1 lost, 1 lost)" diceSize={26} fontSize={14} color={ink} />
        </View>
        <View testID="visual-connection-notice" collapsable={false} style={{ height: 46 }}>
          <GameBanner message="Reconnecting… check your network" topInset={0} />
        </View>
        <View testID="visual-turn-notice" collapsable={false}>
          <NotificationBannerContent title="Your turn" body="Pixel Native QA 710 is waiting for your move" height={notificationHeight} onPress={() => {}} onDismiss={() => {}} />
        </View>
      </View>
    </ScrollView>
  )
}
